import { z } from "zod";
import type { AuthPermissionResource, AuthUserRelationType } from "@/lib/api/authUsers/users";
import type { UpdateAuthUserRequest } from "@/lib/validation/authUsers";
import { crmErrorReason, crmValidationIssues, type FailedResult } from "./crmErrors";

export interface AuthUserFormErrors {
  form: string | null;
  permissions: Partial<Record<AuthPermissionResource, string>>;
  relations: Partial<Record<AuthUserRelationType, string>>;
}

export const noAuthUserErrors: AuthUserFormErrors = { form: null, permissions: {}, relations: {} };

const PermissionDeniedDataSchema = z.object({ resource: z.string(), action: z.string() });

const messagesByReason: Record<string, string> = {
  internal_auth_user: "Internal staff login. Permissions are managed elsewhere.",
  auth_user_not_found: "This login no longer exists.",
};

/** A readable message for a failed mobile login read or save. */
export function describeAuthUserError(result: FailedResult, fallback: string): string {
  const reason = crmErrorReason(result.data);
  // 403 bodies of the general API's internal route guard.
  if (PermissionDeniedDataSchema.safeParse(result.data).success || reason === "internal_account_required") {
    return "You don't have permission to edit mobile logins.";
  }
  return (reason ? messagesByReason[reason] : undefined) ?? (result.message || fallback);
}

/** Schema issues go on the permission or relation type at their path; anything else is a form error. */
export function authUserSaveErrors(result: FailedResult, request: UpdateAuthUserRequest): AuthUserFormErrors {
  const issues = crmValidationIssues(result.data);
  if (!issues) {
    return { ...noAuthUserErrors, form: describeAuthUserError(result, "The changes couldn't be saved. Try again.") };
  }

  const errors: AuthUserFormErrors = { form: null, permissions: {}, relations: {} };
  for (const issue of issues) {
    const [field, index] = issue.path;
    const permission = field === "permissions" && typeof index === "number" ? request.permissions[index] : undefined;
    const relation = field === "relations" && typeof index === "number" ? request.relations[index] : undefined;
    if (permission) {
      errors.permissions[permission.resource] = errors.permissions[permission.resource] ?? issue.message;
    } else if (relation) {
      errors.relations[relation.relation_type] = errors.relations[relation.relation_type] ?? issue.message;
    } else {
      errors.form = errors.form ?? issue.message;
    }
  }
  return { ...errors, form: errors.form ?? "Some changes weren't accepted. Check the highlighted fields." };
}
